import { useMemo, useState } from 'react';
import { Button, Card, Table, Space, Form, Input, Select, Tag, App } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, AppstoreOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { trpc } from '@/lib/api';
import { withDevModalFilePath, NumericInput } from '../common';

type TemplatePart = {
  id: string;
  partId: string;
  expectedQuantity: number;
  notes: string | null;
  part: {
    id: string;
    displayId: string;
    name: string;
    nameKana: string | null;
    partNumber: string | null;
    manufacturer: string | null;
    unit: string;
    currentStockLevel: number;
    optimalStockLevel: number;
    category: string | null;
  };
};

type Part = {
  id: string;
  displayId: string;
  name: string;
  nameKana: string | null;
  partNumber: string | null;
  manufacturer: string | null;
  unit: string;
  currentStockLevel: number;
  optimalStockLevel: number;
  category: string | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizePart = (value: unknown): Part | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    displayId: typeof value.displayId === 'string' ? value.displayId : value.id,
    name: typeof value.name === 'string' ? value.name : '名称未設定',
    nameKana: typeof value.nameKana === 'string' ? value.nameKana : null,
    partNumber: typeof value.partNumber === 'string' ? value.partNumber : null,
    manufacturer: typeof value.manufacturer === 'string' ? value.manufacturer : null,
    unit: typeof value.unit === 'string' ? value.unit : '個',
    currentStockLevel: typeof value.currentStockLevel === 'number' ? value.currentStockLevel : 0,
    optimalStockLevel: typeof value.optimalStockLevel === 'number' ? value.optimalStockLevel : 0,
    category: typeof value.category === 'string' ? value.category : null,
  };
};

const normalizePartList = (value: unknown): Part[] => {
  const candidates =
    Array.isArray(value)
      ? value
      : isRecord(value) && Array.isArray(value.items)
        ? value.items
        : [];

  return candidates
    .map((item) => normalizePart(item))
    .filter((item): item is Part => item !== null);
};

const normalizeTemplatePart = (value: unknown): TemplatePart | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.partId !== 'string') {
    return null;
  }

  const part = normalizePart(value.part);
  if (!part) {
    return null;
  }

  return {
    id: value.id,
    partId: value.partId,
    expectedQuantity: typeof value.expectedQuantity === 'number' ? value.expectedQuantity : 1,
    notes: typeof value.notes === 'string' ? value.notes : null,
    part,
  };
};

const normalizeTemplatePartList = (value: unknown): TemplatePart[] => {
  const candidates =
    Array.isArray(value)
      ? value
      : isRecord(value) && Array.isArray(value.items)
        ? value.items
        : isRecord(value) && Array.isArray(value.data)
          ? value.data
          : [];

  return candidates
    .map((item) => normalizeTemplatePart(item))
    .filter((item): item is TemplatePart => item !== null);
};

interface Props {
  templateId: string;
  companyId?: string;
}

const { Modal } = withDevModalFilePath('client/src/components/work-order/TemplatePartsSection.tsx');

export function TemplatePartsSection({ templateId }: Props) {
  const { message } = App.useApp();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedPart, setSelectedPart] = useState<TemplatePart | null>(null);
  const [form] = Form.useForm();

  // データ取得
  const { data: templatePartsData, refetch } = trpc.workOrderTemplate.getParts.useQuery(
    { templateId },
    { enabled: Boolean(templateId) }
  );

  // parts.list は認証コンテキストの companyId を使うため、companyId 未取得でも取得する
  const partsListQueryInput = {
    page: 1,
    limit: 500,
  };
  const { data: partsListData } = trpc.parts.list.useQuery(partsListQueryInput, {
    enabled: Boolean(templateId),
  });

  const templateParts = useMemo(
    () => normalizeTemplatePartList(templatePartsData),
    [templatePartsData]
  );
  const partsList = useMemo(
    () => normalizePartList(partsListData),
    [partsListData]
  );

  // Mutations
  const addPartMutation = trpc.workOrderTemplate.addPart.useMutation({
    onSuccess: () => {
      message.success('部品・消耗品を追加しました');
      setAddModalOpen(false);
      form.resetFields();
      void refetch();
    },
    onError: (error) => {
      message.error(`エラー: ${error.message}`);
    },
  });

  const updatePartMutation = trpc.workOrderTemplate.updatePart.useMutation({
    onSuccess: () => {
      message.success('部品・消耗品情報を更新しました');
      setEditModalOpen(false);
      form.resetFields();
      setSelectedPart(null);
      void refetch();
    },
    onError: (error) => {
      message.error(`エラー: ${error.message}`);
    },
  });

  const removePartMutation = trpc.workOrderTemplate.removePart.useMutation({
    onSuccess: () => {
      message.success('部品・消耗品を削除しました');
      void refetch();
    },
    onError: (error) => {
      message.error(`エラー: ${error.message}`);
    },
  });

  // ハンドラー
  const handleAddPart = () => {
    form.resetFields();
    setAddModalOpen(true);
  };

  const handleAddSubmit = async () => {
    try {
      const values = await form.validateFields();
      addPartMutation.mutate({
        templateId,
        partId: values.partId,
        expectedQuantity: values.expectedQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleEdit = (record: TemplatePart) => {
    setSelectedPart(record);
    form.setFieldsValue({
      expectedQuantity: record.expectedQuantity,
      notes: record.notes,
    });
    setEditModalOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedPart) return;

    try {
      const values = await form.validateFields();
      updatePartMutation.mutate({
        templateId,
        partLinkId: selectedPart.id,
        expectedQuantity: values.expectedQuantity,
        notes: values.notes || null,
      });
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleDelete = (record: TemplatePart) => {
    Modal.confirm({
      title: '部品・消耗品を削除しますか？',
      content: `「${record.part.name}」をこのテンプレートから削除します。`,
      okText: '削除',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: () => {
        removePartMutation.mutate({
          templateId,
          partLinkId: record.id,
        });
      },
    });
  };

  const getStockStatusTag = (current: number, optimal: number) => {
    if (current >= optimal) {
      return <Tag color="green">十分</Tag>;
    } else if (current >= optimal * 0.5) {
      return <Tag color="orange">少ない</Tag>;
    } else {
      return <Tag color="red">不足</Tag>;
    }
  };

  const columns: ColumnsType<TemplatePart> = [
    {
      title: '部品・消耗品名',
      key: 'name',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <strong>{record.part.name}</strong>
          {record.part.partNumber && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              型式: {record.part.partNumber}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'メーカー',
      dataIndex: ['part', 'manufacturer'],
      width: 150,
      render: (val) => val || '-',
    },
    {
      title: '想定数量',
      dataIndex: 'expectedQuantity',
      width: 120,
      render: (qty, record) => `${qty} ${record.part.unit}`,
    },
    {
      title: '現在在庫',
      key: 'stock',
      width: 150,
      render: (_, record) => (
        <Space>
          {record.part.currentStockLevel} {record.part.unit}
          {getStockStatusTag(record.part.currentStockLevel, record.expectedQuantity)}
        </Space>
      ),
    },
    {
      title: 'カテゴリ',
      dataIndex: ['part', 'category'],
      width: 100,
      render: (val) => val || '-',
    },
    {
      title: '備考',
      dataIndex: 'notes',
      width: 200,
      ellipsis: true,
      render: (val) => val || '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            編集
          </Button>
          <Button
            size="small"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <AppstoreOutlined />
            必要部品・消耗品
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddPart}
          >
            部品・消耗品を追加
          </Button>
        }
      >
        <Table<TemplatePart>
          columns={columns}
          dataSource={templateParts}
          rowKey="id"
          scroll={{ x: 1000 }}
          pagination={false}
        />
      </Card>

      {/* 部品・消耗品追加モーダル */}
      <Modal
        title="部品・消耗品を追加"
        open={addModalOpen}
        onOk={handleAddSubmit}
        onCancel={() => {
          setAddModalOpen(false);
          form.resetFields();
        }}
        confirmLoading={addPartMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="部品・消耗品"
            name="partId"
            rules={[{ required: true, message: '部品・消耗品を選択してください' }]}
          >
            <Select
              showSearch
              placeholder="部品・消耗品を選択"
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={partsList.map((part) => ({
                label: `${part.name} ${part.partNumber ? `(${part.partNumber})` : ''} - 在庫: ${part.currentStockLevel}${part.unit}`,
                value: part.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="想定使用数量"
            name="expectedQuantity"
            rules={[{ required: true, message: '数量を入力してください' }]}
            initialValue={1}
          >
            <NumericInput min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item label="備考" name="notes">
            <Input.TextArea rows={3} placeholder="備考を入力（任意）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 部品・消耗品編集モーダル */}
      <Modal
        title="部品・消耗品情報を編集"
        open={editModalOpen}
        onOk={handleEditSubmit}
        onCancel={() => {
          setEditModalOpen(false);
          form.resetFields();
          setSelectedPart(null);
        }}
        confirmLoading={updatePartMutation.isPending}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item label="部品・消耗品名">
            <Input disabled value={selectedPart?.part.name} />
          </Form.Item>

          <Form.Item
            label="想定使用数量"
            name="expectedQuantity"
            rules={[{ required: true, message: '数量を入力してください' }]}
          >
            <NumericInput min={1} style={{ width: '100%' }} addonAfter={selectedPart?.part.unit} />
          </Form.Item>

          <Form.Item label="備考" name="notes">
            <Input.TextArea rows={3} placeholder="備考を入力（任意）" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
